/**
 * This handset's own identity and its own counter, which are not the platform's.
 *
 * Two of the operations the new screens press require a fact only the device can supply, and
 * neither is a platform identifier:
 *
 *   - **`deviceId`** on DRV-06's observation. Required, a free string, and its purpose is to tell
 *     two handsets apart when one courier reports from both: the service keys its latest position
 *     on the driver and orders by `sequence`, so two devices sharing an identifier would interleave
 *     into one sequence and the stale one would overwrite the moving one.
 *   - **`sequence`** on the same observation, and **`sourceSequence`** on LOG-06's scan. Both are
 *     required integers and both are the sender's, which is the whole reason neither operation takes
 *     an `Idempotency-Key`: LOG-06 answers `duplicate` for a sequence it has seen and `stale` for
 *     one behind the latest, and DRV-06 answers `replayed`. A counter that restarted at one on every
 *     reload would make every scan after a reload a duplicate.
 *
 * So both live in `localStorage`, and both are **this device's state rather than a cache of the
 * platform's**, which is the same distinction `offline-queue.ts` turns on. Nothing here is read back
 * from a service and nothing here is shown to a courier.
 *
 * Every access is wrapped: a private window, blocked site data or a quota refusal throws on read as
 * readily as on write, and a courier whose browser refuses storage must still be able to send a
 * scan. The fallback is an identifier for this page's lifetime and a counter seeded from the clock,
 * which is worse — a reload then looks like a second device — and is better than a screen that
 * cannot send anything. It is reported rather than hidden: `deviceIdentity().persistent` says which
 * of the two happened and `5 Scan` says so in its flag.
 */

const DEVICE_KEY = "tdelivr.driver-app.device-id";
const SEQUENCE_KEY = "tdelivr.driver-app.sequence";

export interface DeviceIdentity {
  readonly deviceId: string;
  /** False when this identifier will not survive a reload, which changes what a duplicate means. */
  readonly persistent: boolean;
}

/**
 * This device's identifier, made once and kept.
 *
 * A UUID rather than anything derived from the browser: a fingerprint would identify the *person*
 * across sessions, which is a thing a delivery application has no business computing, and would be
 * wrong anyway the moment two couriers shared a handset.
 */
export function deviceIdentity(storage: Storage | undefined = safeStorage()): DeviceIdentity {
  if (storage === undefined) return { deviceId: freshId(), persistent: false };
  try {
    const held = storage.getItem(DEVICE_KEY);
    if (held !== null && held.length > 0) return { deviceId: held, persistent: true };
    const made = freshId();
    storage.setItem(DEVICE_KEY, made);
    return { deviceId: made, persistent: true };
  } catch {
    return { deviceId: freshId(), persistent: false };
  }
}

/**
 * The next number in this device's own sequence, which both operations read as monotonic.
 *
 * Allocated and stored before it is used, not after: a counter written only once the request
 * succeeds would re-issue the same number to the retry of a request that actually landed, and
 * LOG-06 would answer `duplicate` to a scan that was not one. Sequence numbers are cheap and holes
 * in them are not an error for DRV-06; a hole *is* an error for LOG-06, which is why
 * `offline-queue.ts` sends in order and stops at the first failure rather than skipping.
 */
export function nextSequence(
  storage: Storage | undefined = safeStorage(),
  now: () => number = () => Date.now(),
): number {
  if (storage === undefined) return seedFromClock(now);
  try {
    const held = Number(storage.getItem(SEQUENCE_KEY) ?? "");
    const next = Number.isSafeInteger(held) && held > 0 ? held + 1 : seedFromClock(now);
    storage.setItem(SEQUENCE_KEY, String(next));
    return next;
  } catch {
    return seedFromClock(now);
  }
}

/**
 * Where a counter starts when there is nothing to carry on from.
 *
 * Whole seconds since the epoch, which is monotonic, fits a safe integer for the next two hundred
 * years, and cannot collide with the previous run's numbers the way a restart at one would. It is
 * not an instant anybody reads — DRV-06 and LOG-06 both carry their own `observedAt`/`occurredAt`
 * for that — and the only property asked of it is that it is larger than last time.
 */
function seedFromClock(now: () => number): number {
  return Math.floor(now() / 1000);
}

function freshId(): string {
  return globalThis.crypto.randomUUID();
}

/** `localStorage`, or nothing, because reading the property itself throws in some configurations. */
function safeStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
